import { flat } from '@xmcl/text-component';
import PItem from 'prismarine-item';
import { renderSlot } from 'minecraft-renderer/src/three/renderSlot';
import { getItemModelName, getItemNameRaw } from '../../mineflayer/items';
import { inventoryBundledConfig } from './inventoryTexturesConfig';
// ----- Atlas sprite extraction (for item textures with resource pack support) -----
const spriteCache = new Map();
/** Clear sprite extraction cache (call when atlases are updated). */
export function clearInventoryCaches() {
    spriteCache.clear();
    inventoryBundledConfig.resetRenderedSlots();
}
function getAtlas(texture) {
    if (!appViewer?.resourcesManager)
        return null;
    const r = appViewer.resourcesManager;
    if (texture === 'gui')
        return (r.currentResources?.guiAtlas?.image ?? null);
    if (texture === 'items')
        return (r.currentResources?.itemsAtlasImage ?? null);
    if (texture === 'blocks')
        return (r.currentResources?.blocksAtlasImage ?? null);
    return null;
}
/** Get atlas source suitable for the block renderer (accepts string data URLs). */
function getAtlasForBlockRenderer(texture) {
    if (!appViewer?.resourcesManager)
        return null;
    const r = appViewer.resourcesManager;
    if (texture === 'blocks')
        return (r.blocksAtlasParser?.latestImage ?? null);
    return null;
}
/** Extract a single-face sprite from the GUI or items atlas as a data URL. */
export function extractSpriteDataUrl(texture, slice) {
    const atlas = getAtlas(texture);
    if (!atlas || !slice)
        return undefined;
    const [x, y, w, h] = slice;
    const cacheKey = `${texture}:${x}:${y}:${w}:${h}`;
    if (spriteCache.has(cacheKey))
        return spriteCache.get(cacheKey);
    try {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(atlas, x, y, w, h, 0, 0, w, h);
        const url = canvas.toDataURL();
        spriteCache.set(cacheKey, url);
        return url;
    }
    catch {
        return undefined;
    }
}
/** Build an isometric BlockTextureRender from blockData returned by renderSlot. */
export function buildBlockTexture(blockData) {
    const source = getAtlasForBlockRenderer('blocks');
    if (!source)
        return undefined;
    const getFace = (...names) => {
        for (const n of names) {
            const face = blockData[n];
            if (face?.slice)
                return { slice: face.slice };
        }
        return undefined;
    };
    const top = getFace('top', 'up', 'all', 'south', 'east', 'west', 'north');
    if (!top)
        return undefined;
    const left = getFace('left', 'east', 'west', 'all') ?? top;
    const right = getFace('right', 'north', 'south', 'all') ?? top;
    return {
        source: source,
        top,
        left,
        right,
    };
}
// ----- Shared texture resolution (single source of truth for all item surfaces) -----
/**
 * Resolve `texture` / `blockTexture` for any item by name + optional nbt/components.
 * Used by buildItemMapper (live slots), enrichItemStack (JEI / recipes).
 * Returns empty object on failure so callers can spread safely.
 */
export function resolveItemTextures(item) {
    if (!appViewer?.resourcesManager?.currentResources)
        return {};
    try {
        const modelName = getItemModelName({ name: item.name, nbt: item.nbt ?? null, components: item.components }, { 'minecraft:display_context': 'gui' }, appViewer.resourcesManager, appViewer.playerState.reactive);
        const slotProps = renderSlot({ modelName, originalItemName: item.name }, appViewer.resourcesManager);
        if (slotProps.blockData) {
            const blockTexture = buildBlockTexture(slotProps.blockData);
            if (blockTexture)
                return { blockTexture };
        }
        else if (slotProps.slice) {
            const texture = extractSpriteDataUrl(slotProps.texture, slotProps.slice);
            if (texture)
                return { texture };
        }
        return {};
    }
    catch {
        return {};
    }
}
/**
 * Mutate an ItemStack in place with texture/blockTexture.
 * Convenience wrapper around resolveItemTextures for JEI / recipe callers.
 */
export function enrichItemStack(item) {
    if (!item.name)
        return;
    const resolved = resolveItemTextures({ name: item.name, nbt: item.nbt, components: item.components });
    if (resolved.texture !== undefined)
        item.texture = resolved.texture;
    if (resolved.blockTexture !== undefined)
        item.blockTexture = resolved.blockTexture;
}
// ----- Item mapper – enriches raw bot slots with textures and display info -----
export function buildItemMapper(version) {
    const PrismarineItem = PItem(version);
    return (raw, mapped) => {
        try {
            const slot = new PrismarineItem(raw.type, raw.count, raw.metadata ?? 0);
            if (raw.nbt)
                slot.nbt = raw.nbt;
            if (raw.components)
                slot.components = raw.components;
            const { texture, blockTexture } = resolveItemTextures({
                name: slot.name,
                nbt: slot.nbt,
                components: raw.components,
            });
            const nameRaw = getItemNameRaw(slot, appViewer.resourcesManager);
            const displayName = nameRaw
                ? flat(nameRaw).map((p) => (typeof p === 'string' ? p : p.text)).join('')
                : slot.displayName;
            return {
                ...mapped,
                name: slot.name,
                displayName,
                texture,
                blockTexture,
                durability: (typeof slot.maxDurability === 'number' && typeof slot.durabilityUsed === 'number')
                    ? slot.maxDurability - slot.durabilityUsed
                    : undefined,
                maxDurability: (slot.maxDurability ?? undefined),
                enchantments: slot.enchants?.map((e) => ({ name: e.name, level: e.lvl })),
            };
        }
        catch {
            return mapped;
        }
    };
}
// ----- Texture config – delegates GUI lookups to inventoryBundledConfig -----
export const textureConfig = {
    getGuiTextureUrl: (path) => inventoryBundledConfig.getGuiTextureUrl(path),
    getItemTextureUrl(_item) {
        return '';
    },
    getBlockTextureUrl(_item) {
        return '';
    },
};
// ----- Window title formatter – resolves JSON text components to display strings -----
/** Parse a raw window title (JSON string, NBT object, or plain text) into a readable string. */
export function formatWindowTitle(rawTitle) {
    if (rawTitle === null || rawTitle === undefined)
        return '';
    if (typeof rawTitle === 'string') {
        // Try to parse JSON text component
        if (rawTitle.startsWith('{') || rawTitle.startsWith('"')) {
            try {
                return formatWindowTitle(JSON.parse(rawTitle));
            }
            catch {
                // Not valid JSON — treat as plain text
            }
        }
        return rawTitle;
    }
    if (typeof rawTitle === 'object') {
        // Handle NBT-simplified format: { value: "...", type: "string" }
        if ('value' in rawTitle && rawTitle.type === 'string') {
            return formatWindowTitle(rawTitle.value);
        }
        // Handle translate key: { translate: "container.chestDouble" }
        if (rawTitle.translate) {
            const lang = globalThis.loadedData?.language;
            return lang?.[rawTitle.translate] ?? rawTitle.translate;
        }
        // Handle text key: { text: "Custom Name" }
        if (typeof rawTitle.text === 'string') {
            return rawTitle.text;
        }
        // Handle extra/with arrays by joining
        if (rawTitle.extra) {
            return rawTitle.extra.map(formatWindowTitle).join('');
        }
        // Fallback: stringify for non-empty objects
        if (typeof rawTitle[''] === 'string')
            return rawTitle[''];
    }
    return String(rawTitle);
}
